import React from 'react';
import { Box, Typography, Select, MenuItem, Tooltip } from '@mui/material';
import { SVG } from 'components/Asset/SVG';
import { ThemePalette, theme } from 'mui.theme';
import { type RequiredHeader } from './FileRequirements';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const { svgIcons } = theme.palette;

interface HeaderMappingProps {
  requiredHeaders: RequiredHeader[];
  csvHeaders: string[];
  mappedHeaders: Record<string, string>;
  duplicateMappings: Record<string, boolean>;
  enableDarkTheme: boolean;
  handleHeaderMappingChange: (
    requiredHeader: string,
    selectedHeader: string
  ) => void;
}

const HeaderMapping: React.FC<HeaderMappingProps> = ({
  requiredHeaders,
  csvHeaders,
  mappedHeaders,
  duplicateMappings,
  enableDarkTheme,
  handleHeaderMappingChange,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.pageBackground
          : palette.pageBackground,
        padding: '10px',
        marginTop: '10px',
        marginBottom: '30px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          marginBottom: '10px',
          display: 'block',
          color: palette.textPrimary,
        }}
      >
        {t('map_csv_headers')}
      </Typography>
      {requiredHeaders.map((header, index) => (
        <Box
          key={header.name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <Typography
            sx={{
              minWidth: 250,
              marginRight: '30px',
              color: palette.textPrimary,
            }}
          >
            {header.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Select
              labelId={`label-${index}`}
              id={`select-${index}`}
              style={{ marginRight: '5px' }}
              value={mappedHeaders[header.name] || ''}
              onChange={(e) => {
                handleHeaderMappingChange(header.name, e.target.value);
              }}
              displayEmpty
              sx={{
                color: palette.textSecondary,
                background: enableDarkTheme
                  ? palette.menuOptions
                  : palette.boxBackground,
              }}
            >
              <MenuItem
                value=""
                sx={{
                  color: palette.textSecondary,
                  background: enableDarkTheme
                    ? palette.menuOptions
                    : palette.boxBackground,
                }}
              >
                <em>{t('select_a_header')}</em>
              </MenuItem>
              {csvHeaders.map((csvHeader) => (
                <MenuItem
                  key={csvHeader}
                  value={csvHeader}
                  sx={{
                    color: palette.textSecondary,
                    background: enableDarkTheme
                      ? palette.menuOptions
                      : palette.boxBackground,
                  }}
                >
                  {csvHeader}
                </MenuItem>
              ))}
            </Select>
            {header.required && (
              <Tooltip title={t('required')} placement="top" arrow>
                <div>
                  {' '}
                  {/* Wrapping div for Tooltip to work correctly with SVG */}
                  <SVG
                    name="redIcon"
                    style={{
                      ...svgIcons?.primary,
                      display:
                        mappedHeaders[header.name] === '' ||
                        duplicateMappings[mappedHeaders[header.name]]
                          ? 'inline'
                          : 'none',
                    }}
                  />
                </div>
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default HeaderMapping;
